import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { BookOpen, Download, CheckCircle, ShoppingCart, Loader2, FileText, Star, Clock, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Book cover images provided by author
const BOOK_COVER_1 = "https://customer-assets.emergentagent.com/job_omw-academy/artifacts/ljfzsvtw_IMG_5405.png";
const BOOK_COVER_2 = "https://customer-assets.emergentagent.com/job_omw-academy/artifacts/ubhzj3a1_IMG_5403.png";

const EbookPage = () => {
  const [ebook, setEbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [purchased, setPurchased] = useState(false);
  const [activeCover, setActiveCover] = useState(0);
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const bookCovers = [BOOK_COVER_1, BOOK_COVER_2];

  useEffect(() => {
    const fetchEbook = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/ebooks/ebook-1`);
        setEbook(response.data);

        if (user) {
          try {
            const purchaseCheck = await axios.get(`${API_URL}/api/ebooks/ebook-1/purchased`, {
              headers: getAuthHeader()
            });
            setPurchased(purchaseCheck.data.purchased);
          } catch (e) {
            console.log("Not purchased");
          }
        }
      } catch (error) {
        console.error("Error fetching ebook:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEbook();
  }, [user, getAuthHeader]);

  const handlePurchase = async () => {
    if (!user) {
      toast.info("Please sign in to purchase");
      navigate("/login", { state: { from: "/ebook" } });
      return;
    }

    setPurchasing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/payments/checkout`,
        {
          ebook_id: "ebook-1",
          origin_url: window.location.origin
        },
        { headers: getAuthHeader() }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Unable to process purchase. Please try again.");
    } finally {
      setPurchasing(false);
    }
  };

  const handleDownload = () => {
    // In a real app, this would download a PDF
    window.open("/ebook-stop-living-paycheck-to-paycheck.md", "_blank");
    toast.success("Ebook download started!");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  const tableOfContents = [
    "The Paycheck Trap: Understanding the Cycle",
    "Purpose Over Paycheck: Your Why Matters",
    "Discovering Your God-Given Assignment",
    "The 60/40 Principle: Living Within Your True Means",
    "Finding Your Monetizable Niche",
    "From Free to Fee: Turning Your Gift into Income",
    "Smart Money Decisions That Change Everything",
    "Creating Your Transition Plan",
    "The Wealthy Place: Where Purpose Meets Prosperity",
    "Your Action Plan: Stop Living Paycheck to Paycheck"
  ];

  return (
    <div data-testid="ebook-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A5F] via-[#162c47] to-[#0f1e32] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Ebook Cover - Using actual book covers */}
            <div className="relative">
              <div className="max-w-md mx-auto">
                <div className="relative">
                  <img 
                    src={bookCovers[activeCover]} 
                    alt="Stop Living Paycheck to Paycheck by Lavista Smith"
                    className="w-full rounded-lg shadow-2xl transform hover:scale-105 transition-transform duration-300"
                  />
                </div>
                {/* Cover switcher */}
                <div className="flex justify-center gap-4 mt-6">
                  {bookCovers.map((cover, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveCover(index)}
                      className={`w-16 h-20 rounded overflow-hidden border-2 transition-all ${
                        activeCover === index ? 'border-[#9D9045] scale-110' : 'border-white/30 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img src={cover} alt={`Cover ${index + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 bg-[#9D9045] text-white px-6 py-3 rounded-full font-bold shadow-lg text-xl">
                ${ebook?.price?.toFixed(2)}
              </div>
            </div>

            {/* Ebook Info */}
            <div className="text-white space-y-6">
              <span className="text-[#9D9045] font-medium text-sm tracking-wider uppercase">Digital Ebook by Lavista Smith</span>
              <h1 className="text-4xl md:text-5xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                Stop Living Paycheck to Paycheck
              </h1>
              <p className="text-xl text-gray-300">
                Real Solutions to Build Wealth
              </p>
              <p className="text-gray-400 leading-relaxed">
                Discover how to break free from the paycheck-to-paycheck cycle by finding your God-given purpose. Learn the 60/40 principle, how to monetize your gifts, and create a transition plan to financial freedom.
              </p>

              <div className="flex flex-wrap gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#9D9045]" />
                  <span>10 Chapters</span>
                </div>
                <div className="flex items-center gap-2">
                  <Download className="h-5 w-5 text-[#9D9045]" />
                  <span>Instant Download</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-[#9D9045]" />
                  <span>Lifetime Access</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                {purchased ? (
                  <Button 
                    onClick={handleDownload}
                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg"
                    data-testid="download-ebook-btn"
                  >
                    <Download className="mr-2 h-5 w-5" />
                    Download Your Ebook
                  </Button>
                ) : (
                  <Button 
                    onClick={handlePurchase}
                    disabled={purchasing}
                    className="bg-[#9D9045] hover:bg-[#8a7e3c] text-white px-8 py-6 text-lg"
                    data-testid="purchase-ebook-btn"
                  >
                    {purchasing ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="mr-2 h-5 w-5" />
                        Buy Now - ${ebook?.price?.toFixed(2)}
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
              What You'll Discover
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              This isn't just another finance book. It's a guide to finding your purpose and letting wealth follow.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: "Purpose Over Paycheck", desc: "Learn why working toward purpose—not just a paycheck—changes everything about your finances" },
              { title: "The 60/40 Principle", desc: "Discover the simple rule: live on 60% of your income and save 40% for freedom" },
              { title: "Find Your Wealthy Place", desc: "Identify your God-given gifts and the unique niche where your purpose meets prosperity" },
              { title: "From Free to Fee", desc: "Turn what you're already doing for free into income-generating opportunities" },
              { title: "Create Your Transition Plan", desc: "Build a roadmap to move from paycheck-dependent to purpose-driven" },
              { title: "Stop Chasing, Start Attracting", desc: "When you chase purpose, wealth overtakes you—learn how to make this shift" }
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl">
                <CheckCircle className="h-6 w-6 text-[#9D9045] flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-[#1E3A5F] mb-1">{item.title}</h4>
                  <p className="text-sm text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Table of Contents
            </h2>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {tableOfContents.map((chapter, i) => (
              <div 
                key={i} 
                className={`flex items-center gap-4 p-4 ${i !== tableOfContents.length - 1 ? 'border-b border-gray-100' : ''}`}
              >
                <span className="h-8 w-8 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                  {i + 1}
                </span>
                <span className="text-gray-700">{chapter}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
              What Readers Are Saying
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                quote: "Lavista's message about purpose over paycheck completely changed my perspective. I went from dreading Mondays to being excited about my work.",
                author: "Marcus J.",
                rating: 5
              },
              {
                quote: "The 60/40 principle was eye-opening. I never knew I should be living on just 60% of my income. Now I'm actually saving for the first time in my life.",
                author: "Tanya R.",
                rating: 5
              },
              {
                quote: "I was doing so much for free! This book helped me identify my gifts and start charging what I'm worth. My side income has doubled.",
                author: "DeShawn M.",
                rating: 5
              }
            ].map((testimonial, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <Star key={j} className="h-5 w-5 fill-[#9D9045] text-[#9D9045]" />
                  ))}
                </div>
                <p className="text-gray-600 italic mb-4">"{testimonial.quote}"</p>
                <p className="font-semibold text-[#1E3A5F]">— {testimonial.author}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1E3A5F]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Take Control of Your Finances?
          </h2>
          <p className="text-gray-300 mb-8 text-lg">
            Stop stressing about money. Start building the financial future you deserve.
          </p>
          {purchased ? (
            <Button 
              onClick={handleDownload}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg"
            >
              <Download className="mr-2 h-5 w-5" />
              Download Your Copy
            </Button>
          ) : (
            <Button 
              onClick={handlePurchase}
              disabled={purchasing}
              className="bg-[#9D9045] hover:bg-[#8a7e3c] text-white px-8 py-6 text-lg"
            >
              {purchasing ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  Get Your Copy - ${ebook?.price?.toFixed(2)}
                </>
              )}
            </Button>
          )}
          <p className="text-gray-400 text-sm mt-4">
            Instant download • Lifetime access • 100% satisfaction guaranteed
          </p>
        </div>
      </section>

      {/* Related Products */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1E3A5F]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Continue Your Learning
            </h2>
            <p className="text-gray-600 mt-2">Check out our complete Money Management course for even deeper training</p>
          </div>

          <div className="flex justify-center">
            <Link to="/school">
              <Button className="bg-[#1E3A5F] hover:bg-[#162c47] text-white px-8 py-6">
                Explore Business School Courses
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EbookPage;
